import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpenIcon, SparklesIcon } from 'lucide-react';
import { DjayAvatar, DjayEmotion } from './DjayAvatar';
import { DjayBubble } from './DjayBubble';
import { useDjaySelection } from './useDjaySelection';
import { useEasterEggs } from './useEasterEggs';
import { useVariantUnlocks } from './useVariantUnlocks';
import { useLearnerContext } from './useLearnerContext';
import { useDjayBubble } from './useDjayBubble';
import { DJAY_TIMINGS } from './config';
import { createRapidTapDetector, createKonamiDetector, checkTimeBasedEgg, getRapidTapReaction } from './easterEggs';
import { useAppStore } from '../app/hooks/useAppStore';
import { useMotionBudget } from '../app/hooks/useMotionBudget';
import { useClickOutside } from '../app/hooks/useClickOutside';
import { usePrevious } from '../app/hooks/usePrevious';
import { soundManager } from '../utils/soundManager';
import { hapticManager } from '../utils/hapticManager';
import { dataLayer } from '../data/dataLayer';
import { DEFAULT_GROUP } from '../config/constants';
import { getDjayPersonalGreeting, getDjayViewMessage, getDjayDailyTip, getDjayIdleMessage } from './djayMessages';
import { useErrorPatterns } from '../utils/errorPatterns';
import { getContextualMenuItems, type PanelMenuItem } from './menuConfig';
import { DjayMenuPanel } from './components/DjayMenuPanel';
import { DjayVariantPicker } from './components/DjayVariantPicker';
// ── Types ────────────────────────────────────────────────────
interface FloatingDjayProps {
  currentView?: string;
  onNavigate?: (path: string) => void;
}
// ── Emotion → Secondary Motion Map ───────────────────────────
// Adds liveliness on top of the avatar's own animation.
// Const-map (audit S2, fixed ronde 2 2026): O(1) lookup, statically
// inspectable. Was previously a switch in a function.
const EMOTION_MOTION: Partial<Record<DjayEmotion, Record<string, unknown>>> = {
  excited: {
    rotate: [0, -6, 6, -4, 4, 0],
    transition: {
      duration: 0.8,
      ease: 'easeInOut' as const
    }
  },
  celebrating: {
    rotate: [0, -10, 10, -8, 8, 0],
    scale: [1, 1.08, 1],
    transition: {
      duration: 0.9,
      ease: 'easeInOut' as const
    }
  },
  proud: {
    scale: [1, 1.1, 1],
    y: [0, -3, 0],
    transition: {
      duration: 0.7,
      ease: 'easeOut' as const
    }
  },
  thinking: {
    rotate: [0, -4, -2, -4, 0],
    transition: {
      duration: 2.4,
      ease: 'easeInOut' as const,
      repeat: Infinity
    }
  },
  pointing: {
    x: [0, -3, 0],
    rotate: [0, -3, 0],
    transition: {
      duration: 0.6,
      ease: 'easeOut' as const
    }
  },
  surprised: {
    scale: [1, 1.15, 0.95, 1],
    transition: {
      duration: 0.5,
      ease: 'easeOut' as const
    }
  },
  encouraging: {
    y: [0, -4, 0, -2, 0],
    transition: {
      duration: 0.8,
      ease: 'easeInOut' as const
    }
  },
  sleeping: {
    rotate: [0, -2, 0],
    transition: {
      duration: 3,
      ease: 'easeInOut' as const,
      repeat: Infinity
    }
  }
};
function getEmotionMotion(emotion: DjayEmotion, shouldAnimate: boolean) {
  if (!shouldAnimate) return undefined;
  return EMOTION_MOTION[emotion];
}
// ── Floating Book Accessory ──────────────────────────────────
function FloatingBook({
  shouldAnimate
}: {
  shouldAnimate: boolean;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.5,
    x: 16,
    y: 8,
    rotate: -30
  }} animate={{
    opacity: 1,
    scale: 1,
    x: 0,
    y: 0,
    rotate: -12
  }} exit={{
    opacity: 0,
    scale: 0.5,
    x: 16,
    y: 8,
    rotate: -30
  }} transition={{
    type: 'spring',
    damping: 18,
    stiffness: 260
  }} className="absolute -left-8 bottom-2 z-0 pointer-events-none" aria-hidden="true" data-id="element-3435">
      <motion.div animate={shouldAnimate ? {
      y: [0, -3, 0],
      rotate: [-12, -8, -12]
    } : undefined} transition={{
      duration: 2.4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="w-9 h-9 bg-gradient-to-br from-amber-100 to-amber-200 rounded-md border-2 border-amber-400 shadow-lg flex items-center justify-center" style={{
      willChange: 'transform'
    }} data-id="element-3436">
        <BookOpenIcon className="w-5 h-5 text-amber-700 stroke-[2.5]" data-id="element-3437" />
      </motion.div>
    </motion.div>;
}
// ── Floating Sparkles Accessory ──────────────────────────────
function FloatingSparkles({
  shouldAnimate
}: {
  shouldAnimate: boolean;
}) {
  if (!shouldAnimate) return null;
  const positions = [{
    x: -14,
    y: -8,
    delay: 0,
    size: 'w-3 h-3'
  }, {
    x: 18,
    y: -14,
    delay: 0.3,
    size: 'w-2.5 h-2.5'
  }, {
    x: -10,
    y: 14,
    delay: 0.6,
    size: 'w-2 h-2'
  }];
  return <div className="absolute inset-0 pointer-events-none z-20" aria-hidden="true" data-id="element-3438">
      {positions.map((p, i) => <motion.div key={i} className={`absolute ${p.size} text-amber-400`} style={{
      left: '50%',
      top: '50%',
      x: p.x,
      y: p.y
    }} initial={{
      opacity: 0,
      scale: 0,
      rotate: 0
    }} animate={{
      opacity: [0, 1, 0],
      scale: [0, 1.2, 0],
      rotate: [0, 180]
    }} exit={{
      opacity: 0,
      scale: 0
    }} transition={{
      duration: 1.4,
      repeat: Infinity,
      delay: p.delay,
      ease: 'easeInOut'
    }} data-id="element-3439">
          <SparklesIcon className="w-full h-full fill-amber-300" data-id="element-3440" />
        </motion.div>)}
    </div>;
}
export function FloatingDjay({
  currentView,
  onNavigate
}: FloatingDjayProps) {
  const {
    activeAccount,
    isDemoMode
  } = useAppStore();
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    selectedVariant,
    setSelectedVariant,
    selectedAccessory,
    selectedTier
  } = useDjaySelection();
  const {
    triggerEgg,
    isDiscovered
  } = useEasterEggs();
  // Long-press discovery is derived from the easter-egg registry on
  // the SAME `useEasterEggs()` instance — calling
  // `useLongPressDiscovered()` would spin up a second instance with
  // separate state that wouldn't see local `triggerEgg` updates
  // (audit S4, ronde 3 2026 — single persistence path).
  const hasDiscoveredLongPress = isDiscovered('long-press-goud');
  const {
    variants: unlockedVariants
  } = useVariantUnlocks(hasDiscoveredLongPress);
  // ── Bubble state via reducer (audit H5 / P2, ronde 2 2026) ──
  const bubble = useDjayBubble();
  // ── Local UI state ─────────────────────────────────────────
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLongPressMenuOpen, setIsLongPressMenuOpen] = useState(false);
  const [showEasterEggLabel, setShowEasterEggLabel] = useState(false);
  const [emotion, setEmotion] = useState<DjayEmotion>('happy');
  const [isPulsing, setIsPulsing] = useState(false);
  const [tapPulse, setTapPulse] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  // ── Refs ───────────────────────────────────────────────────
  const panelRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isLongPressTriggered = useRef(false);
  const hasShownWelcomeRef = useRef(false);
  const welcomeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const easterEggTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const showProactiveMessageRef = useRef<(msg: string, newEmotion: DjayEmotion, duration: number) => void>(() => {});
  const rapidTapRef = useRef(createRapidTapDetector(DJAY_TIMINGS.RAPID_TAP_THRESHOLD, DJAY_TIMINGS.RAPID_TAP_WINDOW_MS, DJAY_TIMINGS.RAPID_TAP_COOLDOWN_MS));
  const konamiRef = useRef(createKonamiDetector());
  const timeEggShownRef = useRef(false);
  // ── Learner context ────────────────────────────────────────
  const learnerContext = useLearnerContext();
  const {
    displayName,
    streak
  } = learnerContext;
  // Ref to latest learnerContext so idle-effect doesn't re-attach
  // global listeners on every streak/XP mutation (audit H3).
  const learnerContextRef = useRef(learnerContext);
  useEffect(() => {
    learnerContextRef.current = learnerContext;
  }, [learnerContext]);
  // ── Click outside ──────────────────────────────────────────
  useClickOutside(panelRef, () => {
    if (isPanelOpen) {
      setIsPanelOpen(false);
      setHoveredItemId(null);
      bubble.hide();
    }
    if (isLongPressMenuOpen) setIsLongPressMenuOpen(false);
  });
  // ── Touch device detection ─────────────────────────────────
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(hover: none)');
    const update = () => setIsTouchDevice(mql.matches);
    update();
    mql.addEventListener?.('change', update);
    return () => mql.removeEventListener?.('change', update);
  }, []);
  // ── Error patterns (now from shared utility, not progress UI) ──
  // NOTE: AppState lives at `appStore.state`, not on `appStore` itself.
  // The previous code passed the store wrapper directly, which silently
  // produced `state.familyMastery === undefined` and an empty array
  // (latent bug fixed in ronde 2 2026).
  const appStore = useAppStore();
  const selectedGroupForErrors = (appStore.activeAccount?.group || DEFAULT_GROUP) as 6 | 7 | 8;
  const errorPatterns = useErrorPatterns(appStore.state, selectedGroupForErrors);
  // Ref holding the latest menuItems so the auto-cycle effect
  // doesn't tear down whenever menu items recompute (audit H2).
  const menuItemsRef = useRef<PanelMenuItem[]>([]);
  // ── Touch auto-cycle (no-hover devices) ────────────────────
  // Same stable-deps trick as the konami listener: depend on
  // `bubble.show` (stable), not the whole `bubble` object.
  useEffect(() => {
    if (!isTouchDevice || !isPanelOpen) return;
    let index = 0;
    const tick = () => {
      const items = menuItemsRef.current;
      if (items.length === 0) return;
      const item = items[index % items.length];
      index += 1;
      setHoveredItemId(item.id);
      bubble.show(item.bubbleHint, item.bubbleColor, 0);
      setEmotion(item.hoverEmotion);
    };
    const startTimer = setTimeout(tick, DJAY_TIMINGS.TOUCH_CYCLE_INITIAL_MS);
    const interval = setInterval(tick, DJAY_TIMINGS.TOUCH_CYCLE_INTERVAL_MS);
    return () => {
      clearTimeout(startTimer);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTouchDevice, isPanelOpen, bubble.show]);
  // ── Proactive message helper ───────────────────────────────
  const showProactiveMessage = (msg: string, newEmotion: DjayEmotion, duration: number) => {
    if (isPanelOpen || isLongPressMenuOpen) return;
    setIsPulsing(true);
    setEmotion(newEmotion);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => {
      setIsPulsing(false);
      bubble.show(msg, 'default', duration);
      soundManager.playDjaySpeak();
    }, DJAY_TIMINGS.ATTENTION_PULSE_MS);
  };
  // Keep ref in sync via effect (audit H4) — render-body mutation is
  // a React anti-pattern; effect-sync runs after commit so listeners
  // always see a consistent post-render snapshot.
  useEffect(() => {
    showProactiveMessageRef.current = showProactiveMessage;
  });
  // ── Account-switch reset (audit RC-6 / RC-7) ───────────────
  // `resetAll()` (audit P3, ronde 4 2026) clears the session-level
  // `triggerCount` too so account B doesn't inherit A's rapid-tap
  // escalation index (which would otherwise jump straight to the
  // "Kietelkampioen" reaction on first tap).
  useEffect(() => {
    hasShownWelcomeRef.current = false;
    timeEggShownRef.current = false;
    rapidTapRef.current.resetAll();
    konamiRef.current.reset();
  }, [activeAccount?.id]);
  // ── Djay-presence policy (audit LEARNER_HOME_FLOW_UX §P1-1) ─
  // On the `home` view, the HomePage PageHero already renders a large,
  // expressive Djay with contextual greeting + emotion. FloatingDjay
  // stays visible as the ambient buddy (tap → menu still works), but
  // suppresses its own proactive bubbles so the surface has ONE primary
  // Djay voice at a time. APP_FEEL §1 P6 ("Eenvoud Wint") + §3.5 niet-
  // storend principe.
  const isHomeView = currentView === 'home';
  // ── Welcome bubble ─────────────────────────────────────────
  // Deps: only `activeAccount?.id` + `isHomeView` (audit-referentie: Ronde 10).
  // Previously `[activeAccount, learnerContext, isHomeView]` — `learnerContext`
  // changes on every XP/streak mutation, causing this effect (and its
  // setTimeout) to be torn down and re-armed mid-flight. We now read the
  // latest learner context from `learnerContextRef.current` inside the
  // setTimeout callback (same pattern as the idle-bubble effect below) so
  // the greeting reflects the most recent state without the deps churn.
  useEffect(() => {
    if (isHomeView) return; // hero already greets — see presence policy above
    if (!hasShownWelcomeRef.current && activeAccount) {
      hasShownWelcomeRef.current = true;
      welcomeTimerRef.current = setTimeout(() => {
        const greeting = getDjayPersonalGreeting(learnerContextRef.current);
        showProactiveMessageRef.current(greeting.text, greeting.emotion, DJAY_TIMINGS.WELCOME_BUBBLE_MS);
      }, DJAY_TIMINGS.WELCOME_DELAY_MS);
    }
    return () => {
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
    };
  }, [activeAccount?.id, isHomeView]);
  // ── View-change bubble (audit S6 — usePrevious) ────────────
  const previousView = usePrevious(currentView);
  useEffect(() => {
    if (currentView && currentView !== previousView && currentView !== 'practice' && currentView !== 'home' &&
    // presence policy: hero owns home greeting
    previousView !== undefined) {
      const viewMsg = getDjayViewMessage(currentView, {
        streak,
        displayName
      });
      showProactiveMessageRef.current(viewMsg.text, viewMsg.emotion, DJAY_TIMINGS.VIEW_CHANGE_BUBBLE_MS);
    }
  }, [currentView, previousView, streak, displayName]);
  // ── Just-in-time error tip surfacing on Voortgang page ─────
  useEffect(() => {
    if (currentView !== 'progress') return;
    if (errorPatterns.length === 0) return;
    if (!activeAccount?.id) return;
    const accountId = activeAccount.id;
    const today = new Date().toISOString().slice(0, 10);
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    dataLayer.getDjayLastErrorTipDate(accountId).then(lastShown => {
      if (cancelled || lastShown === today) return;
      const topPattern = errorPatterns[0];
      const tipText = `Ik zag iets bij ${topPattern.familyName.toLowerCase()}: ${topPattern.recommendation}`;
      timer = setTimeout(() => {
        showProactiveMessageRef.current(tipText, 'encouraging', DJAY_TIMINGS.ERROR_TIP_BUBBLE_MS);
        void dataLayer.saveDjayLastErrorTipDate(accountId, today).catch(() => {});
      }, DJAY_TIMINGS.VIEW_CHANGE_BUBBLE_MS + 800);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [currentView, errorPatterns, activeAccount?.id]);
  // ── Idle detection ─────────────────────────────────────────
  useEffect(() => {
    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        const idleMsg = getDjayIdleMessage(learnerContextRef.current);
        showProactiveMessageRef.current(idleMsg.text, idleMsg.emotion, DJAY_TIMINGS.IDLE_BUBBLE_MS);
      }, DJAY_TIMINGS.IDLE_TIMEOUT_MS);
    };
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('touchstart', resetIdle);
    resetIdle();
    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (easterEggTimerRef.current) clearTimeout(easterEggTimerRef.current);
    };
  }, []);
  // ── Konami code listener ───────────────────────────────────
  // Depend on `bubble.show` (stable via useCallback) rather than the
  // whole `bubble` object (recreated each render) to avoid re-attaching
  // the global keydown listener on every render.
  const bubbleShow = bubble.show;
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (konamiRef.current.handleKey(e.key)) {
        const result = triggerEgg('konami-code');
        if (result.triggered && result.reaction) {
          setEmotion(result.reaction.emotion);
          bubbleShow(result.reaction.text, 'default', DJAY_TIMINGS.KONAMI_BUBBLE_MS);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [triggerEgg, bubbleShow]);
  // ── Time-based egg ─────────────────────────────────────────
  useEffect(() => {
    if (timeEggShownRef.current || !activeAccount) return;
    const timeEgg = checkTimeBasedEgg();
    if (timeEgg) {
      timeEggShownRef.current = true;
      const timer = setTimeout(() => {
        const result = triggerEgg(timeEgg);
        if (result.triggered && result.reaction) {
          showProactiveMessageRef.current(result.reaction.text, result.reaction.emotion, DJAY_TIMINGS.TIME_EGG_BUBBLE_MS);
        }
      }, DJAY_TIMINGS.TIME_EGG_DELAY_MS);
      return () => clearTimeout(timer);
    }
    // Dep is `activeAccount?.id` (not the object) so this effect doesn't
    // re-run on every unrelated `activeAccount` field change. Audit-
    // referentie: Ronde 10.
  }, [activeAccount?.id, triggerEgg]);
  // ── Handlers ───────────────────────────────────────────────
  const handlePointerDown = () => {
    isLongPressTriggered.current = false;
    longPressTimerRef.current = setTimeout(() => {
      isLongPressTriggered.current = true;
      setIsPanelOpen(false);
      setHoveredItemId(null);
      setIsLongPressMenuOpen(true);
      // S4: single persistence path — triggerEgg('long-press-goud') is
      // the only place that records discovery. The egg is `repeatable:
      // false`, so its `triggered=true` return value tells us this is
      // the first long-press. No separate dataLayer save call.
      const result = triggerEgg('long-press-goud');
      if (result.triggered) {
        setShowEasterEggLabel(true);
        if (easterEggTimerRef.current) clearTimeout(easterEggTimerRef.current);
        easterEggTimerRef.current = setTimeout(() => setShowEasterEggLabel(false), DJAY_TIMINGS.EASTER_EGG_LABEL_MS);
        setEmotion('celebrating');
      } else {
        setEmotion('excited');
        soundManager.playDjaySpeak();
        hapticManager.hapticHeavy();
      }
    }, DJAY_TIMINGS.LONG_PRESS_MS);
  };
  const handlePointerUp = () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
  };
  const handleDjayClick = () => {
    if (isLongPressTriggered.current) return;
    setTapPulse(true);
    // Track in `pulseTimerRef` so unmount-cleanup (idle-effect return) can
    // cancel it — prevents `setTapPulse(false)` firing on an unmounted
    // component when the user navigates away mid-pulse. Audit-referentie:
    // Ronde 10.
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => setTapPulse(false), DJAY_TIMINGS.TAP_PULSE_MS);
    const tapResult = rapidTapRef.current.recordTap();
    if (tapResult.inCooldown) {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      return;
    }
    if (tapResult.triggered) {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      const reaction = getRapidTapReaction(tapResult.triggerCount);
      triggerEgg('rapid-tap');
      setIsPanelOpen(false);
      setIsLongPressMenuOpen(false);
      setHoveredItemId(null);
      setEmotion(reaction.emotion);
      bubble.show(reaction.text, 'default', DJAY_TIMINGS.EASTER_EGG_BUBBLE_MS);
      setShowEasterEggLabel(false);
      soundManager.playDjaySpeak();
      hapticManager.hapticCelebration();
      return;
    }
    if (isLongPressMenuOpen) {
      setIsLongPressMenuOpen(false);
      return;
    }
    if (isPanelOpen) {
      setIsPanelOpen(false);
      setHoveredItemId(null);
      bubble.hide();
      return;
    }
    setIsPulsing(false);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    const viewMsg = getDjayViewMessage(currentView || 'home', {
      streak,
      displayName
    });
    setEmotion(viewMsg.emotion);
    setIsPanelOpen(true);
    // Greeting bubble — persists until menu closes (autoDismiss = 0)
    bubble.show(viewMsg.text, 'default', 0);
    soundManager.playDjaySpeak();
  };
  const handleTipClick = () => {
    const tip = getDjayDailyTip();
    setEmotion(tip.emotion);
    setIsPanelOpen(false);
    setHoveredItemId(null);
    bubble.show(tip.text, 'amber', DJAY_TIMINGS.TIP_BUBBLE_MS);
    soundManager.playDjaySpeak();
  };
  const handleErrorTipClick = (tip: string) => {
    setEmotion('encouraging');
    setIsPanelOpen(false);
    setHoveredItemId(null);
    bubble.show(tip, 'violet', DJAY_TIMINGS.TIP_BUBBLE_MS);
    soundManager.playDjaySpeak();
  };
  const handleNavClick = (path: string) => {
    setIsLongPressMenuOpen(false);
    setIsPanelOpen(false);
    setHoveredItemId(null);
    bubble.hide();
    if (onNavigate) onNavigate(path);
  };
  const handleVariantSelect = (variantId: typeof selectedVariant) => {
    setSelectedVariant(variantId);
    setIsLongPressMenuOpen(false);
  };
  const greetingMsg = getDjayViewMessage(currentView || 'home', {
    streak,
    displayName
  });
  const menuItems = getContextualMenuItems(currentView || 'home', learnerContext, errorPatterns, {
    onTipClick: handleTipClick,
    onNavigate: handleNavClick,
    onErrorTipClick: handleErrorTipClick
  });
  // Sync menuItems into ref via effect (audit H2).
  // IMPORTANT: this useEffect MUST be called before the early return
  // below — otherwise navigating to/from `practice` view changes the
  // hook-count and React throws "Rendered fewer hooks than expected"
  // (audit RC-0, 2026 ronde 2).
  useEffect(() => {
    menuItemsRef.current = menuItems;
  }, [menuItems]);
  const isPreAuthView = ['welcome', 'login', 'register', 'onboarding'].includes(currentView || '');
  if (currentView === 'practice' || !activeAccount && !isDemoMode && !isPreAuthView) {
    return null;
  }
  // Hover/focus on a menu item → swap bubble + emotion + accessory
  const handleItemHover = (item: PanelMenuItem) => {
    setHoveredItemId(item.id);
    bubble.show(item.bubbleHint, item.bubbleColor, 0);
    setEmotion(item.hoverEmotion);
    soundManager.playDjaySpeak();
  };
  const handleItemLeave = () => {
    setHoveredItemId(null);
    // Return to greeting
    bubble.show(greetingMsg.text, 'default', 0);
    setEmotion(greetingMsg.emotion);
  };
  // Determine which accessory to show
  const hoveredItem = hoveredItemId ? menuItems.find(m => m.id === hoveredItemId) : null;
  const showBook = isPanelOpen && hoveredItem?.accessory === 'book';
  const showSparkles = isPanelOpen && hoveredItem?.accessory === 'sparkles' || emotion === 'celebrating';
  const emotionMotion = getEmotionMotion(emotion, shouldAnimate);
  const isBubbleVisible = !!bubble.message && !isPanelOpen && !isLongPressMenuOpen && !showEasterEggLabel;
  return <motion.div
  // Mobile bottom uses safe-area-inset to clear iOS home-indicator
  // (audit LEARNER_HOME_FLOW_UX §P2-3). Desktop keeps a fixed 2rem
  // via md:bottom-8 which overrides the arbitrary mobile value.
  className="fixed bottom-[max(1rem,calc(env(safe-area-inset-bottom,0px)+0.5rem))] right-4 md:bottom-8 md:right-8 z-30 flex items-end justify-end pointer-events-none" initial={shouldAnimate ? {
    y: 100,
    opacity: 0
  } : false} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    damping: 15,
    stiffness: 200
  }} data-id="element-3441">
      <div className="relative flex items-end pointer-events-auto" ref={panelRef} data-id="element-3442">
        {/* Expanded Click Panel — Dynamic Menu */}
        <DjayMenuPanel isOpen={isPanelOpen && !isLongPressMenuOpen} items={menuItems} hoveredItemId={hoveredItemId} shouldAnimate={shouldAnimate} onItemHover={handleItemHover} onItemLeave={handleItemLeave} data-id="element-3443" />

        {/* Long Press Quick Nav Menu */}
        <DjayVariantPicker isOpen={isLongPressMenuOpen} variants={unlockedVariants} selectedVariant={selectedVariant} shouldAnimate={shouldAnimate} showEasterEggLabel={showEasterEggLabel} onSelect={handleVariantSelect} data-id="element-3444" />

        {/* Speech Bubble — switches per hovered menu item */}
        <div className="absolute bottom-full right-0 mb-2 origin-bottom-right" data-id="element-3445">
          <DjayBubble message={bubble.message || ''} isVisible={isBubbleVisible} onDismiss={() => {
          if (!isPanelOpen) bubble.hide();
        }} autoDismissMs={bubble.autoDismiss} position="bottom" colorScheme={bubble.color} data-id="element-3446" />
        </div>

        {/* Avatar Button */}
        <motion.button onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} onClick={handleDjayClick} className="relative focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300/50 rounded-full select-none" aria-label="Praat met Djay" whileHover={shouldAnimate ? {
        scale: 1.05
      } : undefined} whileTap={shouldAnimate ? {
        scale: 0.9,
        y: 4
      } : undefined} animate={shouldAnimate && !isPanelOpen && !isLongPressMenuOpen && !bubble.message ? {
        y: [0, -4, 0],
        transition: {
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      } : undefined} style={{
        willChange: 'transform',
        touchAction: 'none'
      }} data-id="element-3447">
          {/* Tap Pulse */}
          <AnimatePresence data-id="element-3448">
            {tapPulse && <motion.div initial={{
            opacity: 0.8,
            scale: 1
          }} animate={{
            opacity: 0,
            scale: 1.5
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="absolute inset-0 border-4 border-orange-300 rounded-full" data-id="element-3449" />}
          </AnimatePresence>

          {/* Attention Pulse Glow */}
          <AnimatePresence data-id="element-3450">
            {isPulsing && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: [0.4, 0.8, 0.4],
            scale: [1, 1.2, 1]
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="absolute inset-0 bg-orange-400 rounded-full blur-md" data-id="element-3451" />}
          </AnimatePresence>

          {/* Floating book accessory */}
          <AnimatePresence data-id="element-3452">
            {showBook && <FloatingBook shouldAnimate={shouldAnimate} data-id="element-3453" />}
          </AnimatePresence>

          {/* Floating sparkles */}
          <AnimatePresence data-id="element-3454">
            {showSparkles && <FloatingSparkles shouldAnimate={shouldAnimate} data-id="element-3455" />}
          </AnimatePresence>

          {/* Shadow */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-black/10 rounded-full blur-sm" data-id="element-3456" />

          {/* Emotion-driven secondary motion wrapper. key={emotion} retriggers cleanly. */}
          <motion.div key={`emotion-${emotion}`} animate={emotionMotion} className="w-12 h-12 md:w-16 md:h-16 relative z-10" style={{
          willChange: 'transform'
        }} data-id="element-3457">
            <DjayAvatar emotion={emotion} variant={selectedVariant} accessory={selectedAccessory} tier={selectedTier} size="xl" className="w-full h-full drop-shadow-md" animate={shouldAnimate} data-id="element-3458" />
          </motion.div>
        </motion.button>
      </div>
    </motion.div>;
}